<template>
  <Dialog
    v-model="visible"
    class="guardian-consent-dialog"
    :title="pendingStatus ? 'Pediatric Consent' : 'Guardian Consent'"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
  >
    <template v-if="pendingStatus">
      <div class="approval-status-row">
        <span class="approval-status-label">Current Approval Status:</span>
        <span class="approval-status-badge">Email Sent - Awaiting Approval</span>
      </div>
    </template>
    <p class="guardian-consent-description">
      Please present the following content to the patient’s legal guardian and acknowledgement and records:
    </p>
    <div class="guardian-consent-box">
      <div class="consent-content">
        <blockquote class="consent-blockquote">
          Your child’s provider has determined that ordering this test may provide valuable information to support your child’s health and wellness. These tests have established reference ranges for adults (≥18 years of age), and pediatric reference ranges have NOT been established for certain tests. By providing consent, you acknowledge that your child’s provider may order the test and is responsible for interpreting the results based on the clinical evaluation of your child.
        </blockquote>
      </div>
      <div class="consent-tabs">
        <div
          class="consent-tab left"
          :class="{active: tab==='onsite'}"
          @click="onTabClick('onsite', $event)"
        >
          <span class="tab-title">Onsite Approval:</span>
          <div class="tab-radios">
            <div class="consent-radio-item" @click.stop="selectOnsite('yes'); tab='onsite'">
              <span class="radio-circle" :class="{checked: onsiteSelection==='yes'}"></span>
              <span>Yes, I consent.</span>
            </div>
            <transition name="fade-slide">
              <div v-if="onsiteSelection==='yes'" class="consent-input-wrap" style="margin-top: -2px; margin-bottom: 8px;">
                <Input
                  v-model="guardianName"
                  placeholder="Guardian's Name"
                  style="width: 260px; margin-left: 0;"
                />
              </div>
            </transition>
            <div class="consent-radio-item" @click.stop="selectOnsite('no'); tab='onsite'">
              <span class="radio-circle" :class="{checked: onsiteSelection==='no'}"></span>
              <span>No, I do not consent.</span>
            </div>
          </div>
          <div class="tab-actions">
            <img class="btn-icon" src="@/assets/orderTest/order-page/print.svg" /> 
            <Button colorType="none-stroke" @click="onPrint">Print Content</Button>
            <img class="btn-icon" style="margin: 0 3px 0 15px;" src="@/assets/orderTest/order-page/link.svg" />
            <Button colorType="none-stroke" @click="openInNewTab">Open in new tab</Button>
          </div>
        </div>
        <div
          class="consent-tab right"
          :class="{active: tab==='email'}"
          @click="onTabClick('email', $event)"
        >
          <span class="tab-title">Email Approval:</span>
          <div class="tab-radios">
            <div class="consent-radio-item" @click.stop="selectEmail('send'); tab='email'">
              <span class="radio-circle" :class="{checked: emailSelection==='send' || emailSelection==='always'}"></span>
              <span>Send to guardian’s email. (Email will be sent after checkout.)</span>
            </div>
            <transition name="fade-slide">
              <div v-if="emailSelection==='send' || emailSelection==='always'" class="consent-input-wrap" style="margin-top: -2px; margin-bottom: 8px;">
                <Input
                  v-model="guardianEmail"
                  placeholder="EMAIL@ADDRESS.COM"
                  style="width: 260px; margin-left: 0;"
                />
              </div>
            </transition>
            <div class="consent-checkbox-item" style="cursor:default;">
              <Checkbox
                :status="emailSelection==='always' ? 'checked' : 'uncheck'"
                @changeStatus="onAlwaysEmailCheckbox"
              >
                Always use email approval for future pediatric orders.
              </Checkbox>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="dob-dashed"></div>
    <div class="guardian-consent-actions">
      <Button
        type="normal"
        colorType="dark-blue"
        :disabled="!canSubmit"
        @click="onSubmit"
      >
        {{ submitButtonText }}
      </Button>
    </div>
  </Dialog>
</template>

<script>
import Dialog from '@/NewLibrary/Dialog.vue'
import Button from '@/NewLibrary/Button.vue'
import Input from '@/NewLibrary/Input.vue'
import Checkbox from '@/NewLibrary/Checkbox.vue'

export default {
  name: 'GuardianConsentDialog',
  components: { Dialog, Button, Input, Checkbox },
  props: {
    modelValue: { type: Boolean, default: false },
    defaultGuardianEmail: { type: String, default: '' },
    defaultAlwaysUseEmail: { type: Boolean, default: false },
    pendingStatus: { type: Boolean, default: false }
  },
  emits: ['update:modelValue', 'submit'],
  data() {
    return {
      tab: 'onsite',
      onsiteSelection: null,
      emailSelection: null,
      guardianName: '',
      guardianEmail: '',
    }
  },
  computed: {
    visible: {
      get() { return this.modelValue },
      set(v) { this.$emit('update:modelValue', v) }
    },
    canSubmit() {
      if (this.tab === 'onsite') {
        return this.onsiteSelection === 'yes' ? !!this.guardianName.trim() : !!this.onsiteSelection;
      } else if (this.tab === 'email') {
        const email = String(this.guardianEmail || '').trim();
        return (this.emailSelection === 'send' || this.emailSelection === 'always') && this.isValidEmail(email);
      }
      return false;
    },
    submitButtonText() {
      if (this.tab === 'email') {
        return 'Send';
      }
      return 'Submit';
    }
  },
  watch: {
    visible(v) {
      if (v) {
        this.onsiteSelection = null;
        if (this.defaultAlwaysUseEmail) {
          this.tab = 'email';
          this.emailSelection = 'always';
          if (!this.guardianEmail && this.defaultGuardianEmail) {
            this.guardianEmail = this.defaultGuardianEmail;
          }
        } else {
          this.tab = 'onsite';
          this.emailSelection = null;
        }
      }
    },
    defaultGuardianEmail(newVal) {
      if (
        this.visible &&
        this.tab === 'email' &&
        (this.emailSelection === 'send' || this.emailSelection === 'always') &&
        !this.guardianEmail &&
        newVal
      ) {
        this.guardianEmail = newVal;
      }
    }
  },
  methods: {
    isValidEmail(email) {
      return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(email || '').trim());
    },
    onTabClick(tab) {
      this.tab = tab;
      if (tab === 'onsite') {
        this.emailSelection = null;
      } else if (tab === 'email') {
        this.onsiteSelection = null;
        if (!this.guardianEmail && this.defaultGuardianEmail) this.guardianEmail = this.defaultGuardianEmail;
      }
    },
    selectOnsite(val) {
      if (this.onsiteSelection === val) {
        this.onsiteSelection = null;
      } else {
        this.onsiteSelection = val;
      }
      this.emailSelection = null;
      this.tab = 'onsite';
    },
    selectEmail(val) {
      if (val === 'send' && this.emailSelection === 'always') {
        this.emailSelection = null;
      } else if (this.emailSelection === val) {
        this.emailSelection = null;
      } else {
        this.emailSelection = val;
        if ((val === 'send' || val === 'always') && !this.guardianEmail && this.defaultGuardianEmail) {
          this.guardianEmail = this.defaultGuardianEmail;
        }
      }
      this.onsiteSelection = null;
      this.tab = 'email';
    },
    onAlwaysEmailCheckbox(status) {
      if (status === 'checked') {
        this.emailSelection = 'always';
      } else {
        if (this.emailSelection === 'always') {
          this.emailSelection = 'send';
        }
      }
      this.onsiteSelection = null;
      this.tab = 'email';
    },
    openInNewTab() {
      const query = {};
      if (this.guardianEmail && this.isValidEmail(this.guardianEmail)) {
        query.defaultGuardianEmail = this.guardianEmail.trim();
      } else if (this.defaultGuardianEmail) {
        query.defaultGuardianEmail = this.defaultGuardianEmail;
      }
      const routeData = this.$router.resolve({ name: 'GuardianConsentStandalonePage', query });
      window.open(routeData.href, '_blank');

      const handler = (event) => {
        if (event && event.data) {
          if (event.data.type === 'guardian-consent') {
            const payload = event.data.payload || {};
            let pediatric_consent_info = { consent_required: true, consent_method: payload.tab };
            if (payload.tab === 'onsite' && payload.onsiteSelection === 'yes') {
              pediatric_consent_info.guardian_name = payload.guardianName ? payload.guardianName.trim() : '';
            }
            if (payload.tab === 'email' && (payload.emailSelection === 'send' || payload.emailSelection === 'always')) {
              pediatric_consent_info.guardian_email = payload.guardianEmail ? payload.guardianEmail.trim().toLowerCase() : '';
              pediatric_consent_info.always_use_email_consent = payload.emailSelection === 'always';
            }
            this.visible = false;
            this.$emit('submit', pediatric_consent_info);
            window.removeEventListener('message', handler);
          } else if (event.data.type === 'guardian-consent-no') {
            this.visible = false;
            window.removeEventListener('message', handler);
          }
        }
      };
      window.addEventListener('message', handler);
    },
    onPrint() {
      const routeData = this.$router.resolve({ name: 'GuardianConsentPrintPage' });
      window.open(routeData.href, '_blank');
    },
    onSubmit() {
      if (this.tab === 'onsite' && this.onsiteSelection === 'no') {
        if (this.pendingStatus) {
          const pediatric_consent_info = {
            consent_method: 'onsite',
            status: 0,
          };
          this.$emit('submit', pediatric_consent_info);
        }
        this.visible = false;
        return;
      }
      this.visible = false;
      let pediatric_consent_info = {
        consent_required: true,
        consent_method: this.tab,
      };
      if (this.tab === 'onsite' && this.onsiteSelection === 'yes') {
        pediatric_consent_info.guardian_name = this.guardianName.trim();
        if (this.pendingStatus) {
          pediatric_consent_info.status = 1;
        }
      }
      if (this.tab === 'email' && (this.emailSelection === 'send' || this.emailSelection === 'always')) {
        pediatric_consent_info.guardian_email = this.guardianEmail.trim().toLowerCase();
        pediatric_consent_info.always_use_email_consent = this.emailSelection === 'always';
        if (this.pendingStatus) {
          pediatric_consent_info.status = 1;
        }
      }
      this.$emit('submit', pediatric_consent_info);
    }
  }
}
</script>

<style scoped>
.fade-slide-enter-active, .fade-slide-leave-active {
  transition: max-height 0.2s , opacity 0.2s;
}
.fade-slide-enter-from, .fade-slide-leave-to {
  opacity: 0;
  max-height: 0;
}
.fade-slide-enter-to, .fade-slide-leave-from {
  opacity: 1;
  max-height: 60px;
}
.consent-input-wrap {
  overflow: hidden;
}
.guardian-consent-dialog {
  min-width: 700px;
}
.guardian-consent-description {
  color: #161616;
  font-size: 15px;
  margin-bottom: 24px;
  white-space: normal;
  word-break: keep-all;
}
.guardian-consent-box {
  background: #fff;
  border-radius: 8px;
  padding: 0 0 24px 0;
}
.consent-content {
  margin-bottom: 32px;
}
.consent-blockquote {
  border-left: 2px solid #C4C4C4;
  text-align: justify;
  margin: 0 0 16px 0;
  padding: 0px 16px;
  color: #333;
  font-size: 15px;
  white-space: normal;
  word-break: keep-all;
}
.consent-tabs {
  display: flex;
  gap: 10px;
}
.consent-tab {
  flex: 1;
  border-radius: 8px;
  border: 1px dashed #C8CDD3;
  background: #fff;
  padding: 18px 20px 12px 20px;
  min-width: 0;
  transition: border-color 0.2s;
  position: relative;
}
.consent-tab.active {
  border: 1px solid #1E4670;
  background: #fff;
  z-index: 1;
}
.tab-title {
  color: var(--color-Grey-Grey-4A, #4A4A4A);
  font-weight: bold;
  font-family: Lato;
  font-size: 15px;
  margin-bottom: 15px;
  display: block;
  white-space: normal;
  word-break: keep-all;
}
.tab-actions {
  display: flex;
  align-items: center;
  margin-top: 8px;
}
.btn-icon {
  flex: 0 0 16px;
  margin-right: 1px;
  display: inline-block;
  vertical-align: middle;
}
.tab-radios {
  margin-bottom: 16px;
}
.tab-radios span {
  color: #333;
}
.consent-radio-item {
  display: grid;
  grid-template-columns: 18px 1fr;
  align-items: center;
  column-gap: 12px;
  cursor: pointer;
  font-size: 15px;
  margin-bottom: 10px;
  user-select: none;
  white-space: normal;
  word-break: keep-all;
}
.consent-checkbox-item {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 15px;
  margin-bottom: 10px;
  user-select: none;
  white-space: normal;
  word-break: keep-all;
  margin-left: -4px;
}
.radio-circle {
  width: 18px;
  height: 18px;
  border: 1px solid #1E4670;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
}
.radio-circle.checked::after {
  content: '';
  display: block;
  width: 10px;
  height: 10px;
  background: #1E4670;
  border-radius: 50%;
}
.guardian-consent-actions {
  display: flex;
  justify-content: center;
  margin-top: 24px;
}

.dob-dashed {
  border-bottom: 1px dashed #C8CDD3;
  height: 0;
  margin-top: 6px;
}
.approval-status-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  margin-top: 2px;
  gap: 8px;
}
.approval-status-label {
  color: #4A4A4A;
  font-family: Lato, Arial, sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.14px;
  line-height: normal;
  margin-right: 8px;
}
.approval-status-badge {
  background: #FFF6E6;
  color: #FF9900;
  border-radius: 20px;
  padding: 4px 16px;
  font-size: 14px;
  font-weight: 700;
  font-family: Lato, Arial, sans-serif;
  line-height: 20px;
  display: flex;
  align-items: center;
  height: 28px;
  white-space: nowrap;
  word-break: keep-all;
}
</style>
